<template>
  <article
    class="product-view"
    :class="{ 'product-view--web-masked': isFullyWebMasked }"
  >
    <div class="product-summary">
      <div class="product-media">
        <div class="product-image-wrapper">
          <img
            :src="coverUrl"
            :alt="`${product.title || 'Product'} cover`"
            class="product-cover"
            loading="lazy"
            @error="useDefaultCover"
          />
        </div>
      </div>

      <div class="product-details">
        <div class="product-heading">
          <div>
            <div class="product-title-row">
              <h2 class="product-title">
                <a
                  v-if="productShareUrl"
                  :href="productShareUrl"
                  class="product-title-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {{ product.title || "Untitled" }}
                </a>
                <template v-else>{{ product.title || "Untitled" }}</template>
              </h2>
              <span
                v-if="isFullyWebMasked"
                class="web-masked-indicator"
                role="img"
                :aria-label="webMaskedStatusMessage"
              >
                <v-icon small>mdi-eye-off-outline</v-icon>
              </span>
            </div>
            <p v-if="product.subtitle" class="product-subtitle">
              {{ product.subtitle }}
            </p>
          </div>

          <div class="product-actions">
            <slot name="actions-before" />
            <span
              v-if="$slots['actions-before'] && productId"
              class="product-action-divider"
            ></span>
            <router-link
              v-if="productId"
              :to="editProductRoute"
              class="product-action-button"
              :aria-label="`Edit ${product.title || 'product'}`"
              title="Edit product"
            >
              <svg
                class="edit-product-icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M4 20h4L19 9a2.8 2.8 0 0 0-4-4L4 16v4Z" />
                <path d="m13.5 6.5 4 4" />
              </svg>
            </router-link>

            <span
              v-if="productId && firstSkuId"
              class="product-action-divider"
            ></span>

            <button
              v-if="firstSkuId"
              type="button"
              class="product-action-button"
              :disabled="isFullyWebMasked"
              :aria-disabled="isFullyWebMasked ? 'true' : undefined"
              :aria-label="
                isFullyWebMasked
                  ? webMaskedStatusMessage
                  : `Copy link for ${product.title || 'product'}`
              "
              :title="
                isFullyWebMasked ? webMaskedStatusMessage : 'Copy product link'
              "
              @click="copyProductLink"
            >
              <svg
                class="share-product-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="22"
                height="26"
                fill="currentColor"
                stroke="currentColor"
                viewBox="0 0 22 26"
                aria-hidden="true"
              >
                <path
                  d="M7.66667 12.3333C7.66667 13.2174 7.31548 14.0652 6.69036 14.6904C6.06523 15.3155 5.21739 15.6667 4.33333 15.6667C3.44928 15.6667 2.60143 15.3155 1.97631 14.6904C1.35119 14.0652 1 13.2174 1 12.3333C1 11.4493 1.35119 10.6014 1.97631 9.97631C2.60143 9.35119 3.44928 9 4.33333 9C5.21739 9 6.06523 9.35119 6.69036 9.97631C7.31548 10.6014 7.66667 11.4493 7.66667 12.3333Z"
                  stroke-width="1.25"
                />
                <path
                  d="M14.76 19.404L7.66669 14.7213M14.8934 6.12134L7.80002 10.8013"
                  stroke-width="1.25"
                  stroke-linecap="round"
                />
                <path
                  d="M21 21.6667C21 22.5507 20.6488 23.3986 20.0237 24.0237C19.3985 24.6488 18.5507 25 17.6666 25C16.7826 25 15.9347 24.6488 15.3096 24.0237C14.6845 23.3986 14.3333 22.5507 14.3333 21.6667C14.3333 20.7826 14.6845 19.9348 15.3096 19.3096C15.9347 18.6845 16.7826 18.3333 17.6666 18.3333C18.5507 18.3333 19.3985 18.6845 20.0237 19.3096C20.6488 19.9348 21 20.7826 21 21.6667ZM21 4.33333C21 5.21739 20.6488 6.06523 20.0237 6.69036C19.3985 7.31548 18.5507 7.66667 17.6666 7.66667C16.7826 7.66667 15.9347 7.31548 15.3096 6.69036C14.6845 6.06523 14.3333 5.21739 14.3333 4.33333C14.3333 3.44928 14.6845 2.60143 15.3096 1.97631C15.9347 1.35119 16.7826 1 17.6666 1C18.5507 1 19.3985 1.35119 20.0237 1.97631C20.6488 2.60143 21 3.44928 21 4.33333Z"
                  stroke-width="1.25"
                />
              </svg>
            </button>

            <div
              v-if="showCopiedAlert"
              class="copy-product-alert"
              role="status"
              aria-live="polite"
            >
              <span class="copy-product-alert-text"
                >Product link copied to your clipboard.</span
              >
            </div>
          </div>
        </div>

        <div v-if="hasCredits" class="product-credits">
          <p v-if="product.composer">{{ product.composer }}</p>
          <p v-if="product.arranger && !composerIncludesArranger">
            arr. {{ product.arranger }}
          </p>
          <p v-if="product.lyricist">{{ product.lyricist }}</p>
          <p v-if="product.artist">{{ product.artist }}</p>
        </div>

        <p v-if="description" class="product-description">
          {{ description }}
        </p>

        <p v-if="isFullyWebMasked" class="web-masked-notice">
          This product is not currently visible on jwpepper.com.
          <a
            v-if="webMaskedHelpUrl"
            :href="webMaskedHelpUrl"
            class="web-masked-learn-more"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </p>
      </div>
    </div>

    <div class="sku-table-scroll">
      <table class="sku-table">
        <colgroup>
          <col class="description-column" />
          <col class="delivery-column" />
          <col class="level-column" />
          <col class="preview-column" />
          <col v-if="showPartsUi" class="parts-column" />
          <col class="price-column" />
        </colgroup>
        <thead>
          <tr>
            <th scope="col">Description</th>
            <th scope="col">Delivery</th>
            <th scope="col">Level</th>
            <th scope="col">Preview</th>
            <th v-if="showPartsUi" scope="col">Parts</th>
            <th scope="col">Price</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="skuRow in skuRows" :key="skuRow.key">
            <td class="sku-description">
              <p class="sku-description-title">
                {{ skuDescription(skuRow.sku) }}
              </p>
              <dl class="sku-identifiers">
                <div>
                  <dt>Pepper ID</dt>
                  <dd>{{ skuRow.sku.skuId || "—" }}</dd>
                </div>
                <div v-if="skuRow.sku.pubId">
                  <dt>Supplier ID</dt>
                  <dd>{{ skuRow.sku.pubId }}</dd>
                </div>
              </dl>
            </td>

            <td class="sku-delivery-cell">
              <span class="mobile-label">Delivery</span>
              <span
                class="delivery-badge"
                :class="skuRow.deliveryDisplay.className"
                :title="skuRow.deliveryDisplay.title"
              >
                <img
                  :src="skuRow.deliveryDisplay.icon"
                  :alt="skuRow.deliveryDisplay.alt"
                />
                <span v-if="skuRow.deliveryDisplay.label">
                  {{ skuRow.deliveryDisplay.label }}
                </span>
              </span>
            </td>

            <td class="sku-centered grade-cell">
              <span class="mobile-label">Level</span>
              {{ getGrade(skuRow.sku) || "—" }}
            </td>

            <td class="sku-centered preview-cell">
              <span class="mobile-label">Preview</span>
              <div
                v-if="hasPreviewMedia(skuRow.sku) || getVideoUrl(skuRow.sku)"
                class="media-links"
              >
                <span
                  v-if="hasPreviewMedia(skuRow.sku)"
                  class="media-link"
                  role="link"
                  tabindex="0"
                  :aria-label="`${getPreviewMediaDisplay(skuRow.sku).label} preview for SKU ${skuRow.sku.skuId}`"
                  @click="openExternalUrl(getPreviewMediaUrl(skuRow.sku))"
                  @keydown.enter="
                    openExternalUrl(getPreviewMediaUrl(skuRow.sku))
                  "
                  @keydown.space.prevent="
                    openExternalUrl(getPreviewMediaUrl(skuRow.sku))
                  "
                >
                  <img
                    :src="getPreviewMediaDisplay(skuRow.sku).icon"
                    :class="getPreviewMediaDisplay(skuRow.sku).className"
                    :alt="getPreviewMediaDisplay(skuRow.sku).alt"
                    aria-hidden="true"
                  />
                  <span>{{ getPreviewMediaDisplay(skuRow.sku).label }}</span>
                </span>
                <span
                  v-if="getVideoUrl(skuRow.sku)"
                  class="media-link"
                  role="link"
                  tabindex="0"
                  :aria-label="`Video for SKU ${skuRow.sku.skuId}`"
                  @click="openExternalUrl(getVideoUrl(skuRow.sku))"
                  @keydown.enter="openExternalUrl(getVideoUrl(skuRow.sku))"
                  @keydown.space.prevent="
                    openExternalUrl(getVideoUrl(skuRow.sku))
                  "
                >
                  <img
                    :src="videoIcon"
                    class="video-icon"
                    alt=""
                    aria-hidden="true"
                  />
                  <span>Video</span>
                </span>
              </div>
            </td>

            <td v-if="showPartsUi" class="sku-centered parts-cell">
              <span class="mobile-label">Parts</span>
              <button
                v-if="hasParts(skuRow.sku)"
                type="button"
                class="view-parts-button"
                @click="openPartsDialog(skuRow.sku)"
              >
                View Parts
              </button>
            </td>

            <td class="price-cell">
              <span class="mobile-label">Price</span>
              {{ formatPrice(skuRow.sku.listPrice) }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <v-dialog
      v-if="showPartsUi"
      v-model="partsDialogOpen"
      max-width="980"
      content-class="parts-dialog"
      @click:outside="closePartsDialog"
      @input="!$event && closePartsDialog()"
    >
      <section
        v-if="selectedPartsSku"
        class="parts-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="parts-modal-title"
      >
        <header class="parts-modal__header">
          <div>
            <p class="parts-modal__eyebrow">
              Parts for SKU {{ selectedPartsSku.skuId }}
            </p>
            <h2 id="parts-modal-title" class="parts-modal__title">
              {{ getSkuTitle(selectedPartsSku) }}
            </h2>
          </div>
          <div class="parts-modal__actions">
            <router-link
              :to="partsEditRoute"
              class="parts-modal__edit"
              @click.native="closePartsDialog"
            >
              Edit
            </router-link>
            <button
              type="button"
              class="parts-modal__close"
              aria-label="Close parts"
              @click="closePartsDialog"
            >
              ×
            </button>
          </div>
        </header>

        <p
          v-if="selectedPartsSku.partsSoldSeparately"
          class="parts-modal__sold-separately"
        >
          Parts Sold Separately
        </p>
        <p class="parts-modal__showing">
          Showing: {{ selectedPart.name || "Unnamed part" }}
        </p>

        <div class="parts-modal__content">
          <div class="parts-modal__preview">
            <img
              :src="selectedPart.imageUrl"
              :alt="selectedPart.name || 'Part image'"
              @error="useDefaultPartImage"
            />
          </div>

          <div class="parts-modal__table-wrap">
            <table class="parts-table">
              <thead>
                <tr>
                  <th scope="col">Part</th>
                  <th scope="col">Price</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(part, index) in selectedPartsSku.parts"
                  :key="part.id"
                  :class="{
                    'parts-table__row--selected': index === selectedPartIndex,
                  }"
                  role="button"
                  tabindex="0"
                  @click="selectedPartIndex = index"
                  @keydown.enter="selectedPartIndex = index"
                  @keydown.space.prevent="selectedPartIndex = index"
                >
                  <td>
                    <img
                      :src="part.imageUrl"
                      :alt="''"
                      class="parts-table__thumbnail"
                      @error="useDefaultPartImage"
                    />
                    <span>{{ part.name || "Unnamed part" }}</span>
                  </td>
                  <td>{{ formatPrice(part.price) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </v-dialog>
  </article>
</template>

<script>
import { JWP_PREVIEW_MEDIA_URL, SHOW_SKU_PARTS_UI } from "./constants.js";
import { DEFAULT_COVER_IMAGE_URL } from "./coverImage.js";
import manifestCoverMixin from "./manifestCoverMixin.js";
import { generatedSkuDescription as getGeneratedSkuDescription } from "./skuDescription.js";
import audioPreviewIcon from "./assets/audio-preview-icon.svg";
import digitalDownloadLogo from "./assets/digital-download-logo.svg";
import eprintLogo from "./assets/eprint-logo.svg";
import emailedItemIcon from "./assets/emailed-item-icon.svg";
import previewMediaIcon from "./assets/preview-media-icon.svg";
import scorePreviewIcon from "./assets/score-preview-icon.svg";
import shippedIcon from "./assets/shipped-icon.svg";
import videoIcon from "./assets/video-icon.svg";

const DEFAULT_COVER = DEFAULT_COVER_IMAGE_URL;
const WEB_MASKED_STATUS_MESSAGE =
  "This product is not currently visible on jwpepper.com.";

export default {
  name: "ProductView",
  mixins: [manifestCoverMixin],

  props: {
    product: {
      type: Object,
      required: true,
    },
    productCodes: {
      type: Object,
      default: null,
    },
    webMaskedHelpUrl: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      audioPreviewIcon,
      copiedAlertTimer: null,
      digitalDownloadLogo,
      eprintLogo,
      emailedItemIcon,
      previewMediaIcon,
      scorePreviewIcon,
      showCopiedAlert: false,
      shippedIcon,
      videoIcon,
      partsDialogOpen: false,
      selectedPartsSku: null,
      selectedPartIndex: 0,
    };
  },

  computed: {
    productId() {
      return String(
        this.product.productId ||
          this.product.productGroupId ||
          this.product.skus?.[0]?.skuId ||
          "",
      );
    },

    showPartsUi() {
      return SHOW_SKU_PARTS_UI;
    },

    isFullyWebMasked() {
      const skus = this.product && this.product.skus;
      if (!Array.isArray(skus) || skus.length === 0) {
        return false;
      }
      return skus.every((sku) => sku && sku.webStatus === "M");
    },

    webMaskedStatusMessage() {
      return WEB_MASKED_STATUS_MESSAGE;
    },

    firstSkuId() {
      return String(this.product.skus?.[0]?.skuId || "");
    },

    productShareUrl() {
      if (this.isFullyWebMasked || !this.firstSkuId) {
        return "";
      }

      return `https://www.jwpepper.com/${this.firstSkuId}.item`;
    },

    editProductRoute() {
      return {
        name: "ProductEdit",
        params: { productId: this.productId },
      };
    },

    partsEditRoute() {
      return {
        ...this.editProductRoute,
        query: {
          partsSku: String(this.selectedPartsSku?.skuId || ""),
        },
      };
    },

    description() {
      const description = this.product.description;
      const copy =
        description && typeof description === "object"
          ? description.copy
          : description;

      return this.decodeHtml(copy || "");
    },

    hasCredits() {
      return Boolean(
        this.product.composer ||
        this.product.arranger ||
        this.product.lyricist ||
        this.product.artist,
      );
    },

    composerIncludesArranger() {
      const composer = String(this.product.composer || "").toLowerCase();
      const arranger = String(this.product.arranger || "").toLowerCase();
      return Boolean(arranger && composer.includes(arranger));
    },

    skuRows() {
      const skus = Array.isArray(this.product.skus) ? this.product.skus : [];
      return skus.map((sku, index) => ({
        sku,
        key: sku.skuId || index,
        deliveryDisplay: this.getDeliveryMethodDisplay(sku),
      }));
    },

    selectedPart() {
      const parts = this.selectedPartsSku?.parts || [];
      return (
        parts[this.selectedPartIndex] ||
        parts[0] || {
          imageUrl: DEFAULT_COVER,
          name: "Part",
        }
      );
    },
  },

  watch: {
    product() {
      this.showCopiedAlert = false;
    },
  },

  methods: {
    async copyProductLink() {
      if (this.isFullyWebMasked || !this.productShareUrl) {
        return;
      }

      try {
        if (
          typeof navigator !== "undefined" &&
          navigator.clipboard?.writeText
        ) {
          await navigator.clipboard.writeText(this.productShareUrl);
          this.showProductCopiedAlert();
          return;
        }
      } catch (_error) {
        // Fall through to execCommand when the Clipboard API is unavailable.
      }

      if (this.copyTextWithFallback(this.productShareUrl)) {
        this.showProductCopiedAlert();
      }
    },

    copyTextWithFallback(value) {
      if (typeof document === "undefined") {
        return false;
      }

      const textarea = document.createElement("textarea");
      textarea.value = value;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "fixed";
      textarea.style.top = "-9999px";
      document.body.appendChild(textarea);
      textarea.select();

      let copied = false;
      try {
        copied = Boolean(document.execCommand("copy"));
      } catch (_error) {
        copied = false;
      } finally {
        document.body.removeChild(textarea);
      }

      return copied;
    },

    showProductCopiedAlert() {
      this.showCopiedAlert = true;
      if (this.copiedAlertTimer) {
        window.clearTimeout(this.copiedAlertTimer);
      }
      this.copiedAlertTimer = window.setTimeout(() => {
        this.showCopiedAlert = false;
        this.copiedAlertTimer = null;
      }, 2400);
    },

    hasParts(sku) {
      return Array.isArray(sku?.parts) && sku.parts.length > 0;
    },

    openPartsDialog(sku) {
      if (!SHOW_SKU_PARTS_UI || !this.hasParts(sku)) {
        return;
      }
      this.selectedPartsSku = sku;
      this.selectedPartIndex = 0;
      this.partsDialogOpen = true;
    },

    closePartsDialog() {
      this.partsDialogOpen = false;
      this.selectedPartsSku = null;
      this.selectedPartIndex = 0;
    },

    useDefaultPartImage(event) {
      if (event?.target && event.target.src !== DEFAULT_COVER) {
        event.target.src = DEFAULT_COVER;
      }
    },

    decodeHtml(value) {
      if (typeof document === "undefined") {
        return String(value);
      }

      const element = document.createElement("textarea");
      element.innerHTML = String(value);
      return element.value;
    },

    skuDescription(sku) {
      return (
        getGeneratedSkuDescription(sku, this.productCodes) ||
        this.getSkuTitle(sku)
      );
    },

    getSkuTitle(sku) {
      if (sku.detail) {
        return sku.detail;
      }

      const voice = this.getPrimaryDescription(sku.voices);
      const format = sku.format?.description || sku.format?.code || "";
      const productLine =
        sku.productLine?.description || sku.productLine?.code || "";
      const parts = [voice, format, productLine].filter(
        (value, index, values) => value && values.indexOf(value) === index,
      );

      return parts.join(" • ") || "Music";
    },

    getPrimaryDescription(items) {
      if (!Array.isArray(items) || !items.length) {
        return "";
      }

      const primary = items.find((item) => item.primaryFlag);
      return (primary || items[0]).description || "";
    },

    getGrade(sku) {
      if (!Array.isArray(sku.grades) || !sku.grades.length) {
        return "";
      }

      return sku.grades
        .map((grade) => grade.description || grade.code)
        .filter(Boolean)
        .join(", ");
    },

    getDeliveryMethodKey(sku) {
      const deliveryMethod = String(sku.deliveryMethod || "")
        .trim()
        .toLowerCase();

      if (
        deliveryMethod === "print immediately in my account" ||
        deliveryMethod === "eprint"
      ) {
        return "eprint";
      }
      if (deliveryMethod === "digital download in my account") {
        return "digitalDownload";
      }
      if (deliveryMethod === "purchased item will be emailed") {
        return "emailed";
      }
      if (deliveryMethod === "ships direct from manufacturer") {
        return "shipsDirect";
      }
      if (
        deliveryMethod === "ships from j.w. pepper" ||
        deliveryMethod === "shipped"
      ) {
        return "shipped";
      }

      if (
        String(sku.skuId || "")
          .toUpperCase()
          .endsWith("E")
      ) {
        return "eprint";
      }

      return "shipped";
    },

    getDeliveryMethodDisplay(sku) {
      const deliveryMethodKey = this.getDeliveryMethodKey(sku);
      const displays = {
        shipped: {
          icon: this.shippedIcon,
          alt: "",
          title: "Ships from J.W. Pepper",
          label: "Shipped",
          className: "delivery-badge--shipped",
        },
        shipsDirect: {
          icon: this.shippedIcon,
          alt: "",
          title: "Ships Direct from Manufacturer",
          label: "Shipped",
          className: "delivery-badge--shipped",
        },
        eprint: {
          icon: this.eprintLogo,
          alt: "ePrint",
          title: "Print Immediately in My Account",
          label: "",
          className: "delivery-badge--eprint",
        },
        digitalDownload: {
          icon: this.digitalDownloadLogo,
          alt: "Digital Download",
          title: "Digital Download in My Account",
          label: "",
          className: "delivery-badge--digital-download",
        },
        emailed: {
          icon: this.emailedItemIcon,
          alt: "Purchased Item will be Emailed",
          title: "Purchased Item will be Emailed",
          label: "",
          className: "delivery-badge--emailed",
        },
      };

      return displays[deliveryMethodKey] || displays.shipped;
    },

    hasScore(sku) {
      return Boolean(
        sku.scoreAvailable || sku.hasScore || sku.previewMedia?.scoreAvailable,
      );
    },

    hasAudio(sku) {
      return Boolean(
        sku.audioAvailable || sku.hasAudio || sku.previewMedia?.audioAvailable,
      );
    },

    hasPreviewMedia(sku) {
      return this.hasScore(sku) || this.hasAudio(sku);
    },

    getPreviewMediaDisplay(sku) {
      if (this.hasScore(sku) && this.hasAudio(sku)) {
        return {
          icon: this.previewMediaIcon,
          alt: "",
          label: "Score/Audio",
          className: "preview-media-icon",
        };
      }

      if (this.hasAudio(sku)) {
        return {
          icon: this.audioPreviewIcon,
          alt: "",
          label: "Audio",
          className: "audio-preview-icon",
        };
      }

      return {
        icon: this.scorePreviewIcon,
        alt: "",
        label: "Score",
        className: "score-preview-icon",
      };
    },

    getPreviewMediaUrl(sku) {
      const productId = encodeURIComponent(String(sku.skuId || ""));
      return `${JWP_PREVIEW_MEDIA_URL}?type=audio&productID=${productId}`;
    },

    getVideoUrl(sku) {
      return sku.youtubeUrl || "";
    },

    openExternalUrl(url) {
      if (!url || typeof window === "undefined") {
        return;
      }

      try {
        const parsed = new URL(String(url), window.location.href);
        if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
          return;
        }
        window.open(parsed.toString(), "_blank", "noopener,noreferrer");
      } catch (_error) {
        // Ignore invalid URLs
      }
    },

    formatPrice(price) {
      const numericPrice = Number(price);
      if (!Number.isFinite(numericPrice)) {
        return "—";
      }

      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(numericPrice);
    },
  },
  beforeDestroy() {
    if (this.copiedAlertTimer) {
      window.clearTimeout(this.copiedAlertTimer);
    }
  },
};
</script>

<style scoped>
.product-view {
  width: 100%;
  overflow: hidden;
  border-radius: 0.25rem;
  color: #252525;
  background: #fff;
  box-shadow: 0 10px 30px 0 rgba(0, 0, 0, 0.075);
  border: none;
  padding: 20px;
  max-width: 1220px;
  margin: 0 auto;
}

.product-view--web-masked .product-media,
.product-view--web-masked .product-heading,
.product-view--web-masked .product-credits,
.product-view--web-masked .product-description,
.product-view--web-masked .sku-table-scroll {
  opacity: 0.55;
}

.product-summary {
  display: flex;
  align-items: flex-start;
  gap: 2.25rem;
  padding-bottom: 5rem;
}

.product-media {
  display: flex;
  flex: 0 0 240px;
  align-items: flex-start;
  justify-content: center;
  width: 240px;
  height: 219.6px;
  border: 1px solid #e0dadb;
  background-color: #f6f4f5;
}

.product-image-wrapper {
  display: inline-flex;
  position: relative;
  top: 0.75rem;
  align-items: center;
  width: 174px;
  max-width: 174px;
  height: 225.6px;
  margin: 0 auto;
  color: transparent;
  background-color: #fff;
  box-shadow: 0 10px 10px 0 rgba(0, 0, 0, 0.1);
}

.product-cover {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.product-details {
  flex: 1;
  min-width: 0;
}

.product-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.product-title {
  margin: 0.5rem 0;
  color: #1c1c1c;
  font-size: 24px;
  font-weight: 700;
  line-height: 18.4px;
}

.product-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.web-masked-indicator {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  color: #424242;
}

.web-masked-notice {
  margin: 0.85rem 0 0;
  color: #1c1c1c;
  font-size: 0.9375rem;
  line-height: 1.4;
}

.web-masked-learn-more {
  color: #35647b;
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 0.15rem;
}

.web-masked-learn-more:hover,
.web-masked-learn-more:focus {
  color: #173d50;
}

.product-title-link {
  color: inherit;
  text-decoration: none;
  word-break: break-word;
}

.product-title-link:hover,
.product-title-link:focus {
  text-decoration: underline;
  text-underline-offset: 0.2rem;
}

.product-subtitle {
  margin: 0.4rem 0 0;
  color: #424242;
  font-size: 1rem;
}

.product-actions {
  position: relative;
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  gap: 0.35rem;
}

.product-action-button {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  color: #35647b;
  background: transparent;
  cursor: pointer;
}

.product-action-button:hover,
.product-action-button:focus {
  background: #eef4f6;
}

.product-action-button:disabled {
  color: #707070;
  cursor: not-allowed;
}

.product-action-button:disabled:hover,
.product-action-button:disabled:focus {
  background: transparent;
}

.product-action-divider {
  width: 1px;
  height: 1.7rem;
  background: #d0d0d0;
}

.edit-product-icon {
  width: 1.65rem;
  height: 1.65rem;
  fill: none;
  stroke: currentColor;
  stroke-linecap: round;
  stroke-linejoin: round;
  stroke-width: 1.7;
}

.share-product-icon {
  width: 1.4rem;
  height: 1.65rem;
}

.copy-product-alert {
  position: fixed;
  bottom: 30px;
  left: 30px;
  z-index: 1000;
  width: 400px;
  padding: 8px 16px;
  border-radius: 8px;
  color: #fff;
  background: var(--product-copy-alert-background, #133e4f);
  box-shadow: 0 8px 22px rgba(0, 0, 0, 0.18);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.35;
  text-align: left;
}

.copy-product-alert-text {
  display: inline-block;
  font-size: 22px;
  padding: 8px 16px;
}

.product-credits {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 1.25rem;
  color: #717584;
}

.product-credits p {
  margin: 0;
}

.product-description {
  max-width: 760px;
  margin: 1.5rem 0 0;
  color: #333;
  font-size: 0.9375rem;
  line-height: 1.6;
  border-top: 1px solid #e0dadb;
  padding-top: 16px;
}

.sku-table-scroll {
  width: 100%;
  overflow-x: auto;
  border-top: 1px solid #d0d0d0;
}

.sku-table {
  width: 100%;
  min-width: 860px;
  border-collapse: collapse;
  table-layout: fixed;
}

.description-column {
  width: 36%;
}

.delivery-column {
  width: 15%;
}

.level-column {
  width: 12%;
}

.preview-column {
  width: 15%;
}

.parts-column {
  width: 10%;
}

.price-column {
  width: 12%;
}

.sku-table th {
  padding: 0.8rem 1rem;
  border-bottom: 1px solid #d0d0d0;
  color: #333;
  background: #f3f3f3;
  font-size: 0.8125rem;
  font-weight: 600;
  text-align: left;
}

.sku-table th:not(:first-child) {
  text-align: left;
}

.sku-table td {
  padding: 1rem;
  border-bottom: 1px solid #e0e0e0;
  vertical-align: middle;
  text-align: left;
}

.sku-table tr:last-child td {
  border-bottom: 0;
}

.sku-description-title {
  margin: 0 0 0.65rem;
  color: #222;
  font-size: 0.9375rem;
  font-weight: 600;
}

.sku-identifiers {
  margin: 0;
  color: #555;
  font-size: 0.75rem;
}

.sku-identifiers div {
  display: grid;
  grid-template-columns: 76px 1fr;
  gap: 0.5rem;
  margin-top: 0.2rem;
}

.sku-identifiers dt {
  font-weight: 600;
}

.sku-identifiers dd {
  margin: 0;
}

.view-parts-button {
  margin-top: 0.9rem;
  padding: 0;
  border: 0;
  color: #35647b;
  background: transparent;
  font: inherit;
  font-size: 0.8125rem;
  font-weight: 700;
  text-decoration: underline;
  cursor: pointer;
}

.view-parts-button:hover,
.view-parts-button:focus {
  color: #173d50;
}

.parts-cell .view-parts-button {
  margin-top: 0;
}

.parts-modal {
  overflow: hidden;
  border-radius: 4px;
  background: #fff;
}

.parts-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  color: #fff;
  background: #43414c;
}

.parts-modal__eyebrow {
  margin: 0 0 0.25rem;
  font-size: 0.75rem;
  opacity: 0.8;
}

.parts-modal__title {
  margin: 0;
  font-size: 1.125rem;
  line-height: 1.3;
}

.parts-modal__actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.parts-modal__edit {
  padding: 0.4rem 0.9rem;
  border: 1px solid currentColor;
  border-radius: 3px;
  color: inherit;
  font-size: 0.8125rem;
  font-weight: 700;
  text-decoration: none;
}

.parts-modal__close {
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  color: inherit;
  background: transparent;
  font-size: 2rem;
  font-weight: 300;
  line-height: 1;
  cursor: pointer;
}

.parts-modal__sold-separately {
  margin: 1.25rem 1.5rem 0;
  color: #0c7258;
  font-size: 0.875rem;
  font-weight: 700;
}

.parts-modal__showing {
  margin: 0.5rem 1.5rem 0;
  color: #333;
  font-size: 0.875rem;
  font-weight: 600;
}

.parts-modal__content {
  display: grid;
  grid-template-columns: minmax(220px, 0.9fr) minmax(360px, 1.1fr);
  gap: 1.5rem;
  padding: 1.5rem;
}

.parts-modal__preview {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 340px;
  background: #f4f1f1;
}

.parts-modal__preview img {
  display: block;
  width: 100%;
  height: 340px;
  object-fit: contain;
}

.parts-modal__table-wrap {
  overflow: auto;
  max-height: 340px;
  border: 1px solid #dedede;
}

.parts-table {
  width: 100%;
  border-collapse: collapse;
}

.parts-table th,
.parts-table td {
  padding: 0.85rem;
  border-bottom: 1px solid #e4e4e4;
  text-align: left;
}

.parts-table th {
  color: #333;
  background: #f4f4f4;
  font-size: 0.75rem;
  font-weight: 700;
}

.parts-table td:last-child {
  color: #632a38;
  font-weight: 700;
  white-space: nowrap;
}

.parts-table tbody tr {
  cursor: pointer;
}

.parts-table tbody tr:hover,
.parts-table__row--selected {
  background: #f7f4f4;
  box-shadow: inset 4px 0 #d9313e;
}

.parts-table td:first-child {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.parts-table__thumbnail {
  width: 40px;
  height: 40px;
  object-fit: cover;
}

.sku-centered {
  text-align: center;
}

.sku-delivery-cell {
  text-align: left;
}

.delivery-badge {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.3rem;
  color: #333;
  font-size: 0.75rem;
  text-align: left;
}

.delivery-badge img {
  display: block;
  object-fit: contain;
}

.delivery-badge--shipped img {
  width: 41px;
  height: 22px;
}

.delivery-badge--eprint img {
  width: 63px;
  height: 22px;
}

.delivery-badge--digital-download img {
  width: 119px;
  height: 22px;
}

.delivery-badge--emailed img {
  width: 41px;
  height: 27px;
}

.grade-cell {
  font-size: 0.9375rem;
}

.media-links {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.media-link {
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  min-width: 48px;
  color: #252525;
  font-size: 0.8125rem;
  font-weight: 600;
  cursor: pointer;
}

.media-link:hover,
.media-link:focus {
  opacity: 0.72;
}

.media-link:focus-visible {
  outline: 2px solid #35647b;
  outline-offset: 3px;
  border-radius: 2px;
}

.preview-media-icon {
  width: 31px;
  height: 32px;
}

.audio-preview-icon {
  width: 30px;
  height: 26px;
  margin-top: 3px;
  margin-bottom: 3px;
}

.score-preview-icon {
  width: 24px;
  height: 29px;
  margin-top: 1.5px;
  margin-bottom: 1.5px;
}

.video-icon {
  width: 30px;
  height: 20px;
  margin-top: 6px;
  margin-bottom: 6px;
}

.price-cell {
  font-size: 0.9375rem;
  font-weight: 700;
  text-align: center;
}

.mobile-label {
  display: none;
}

@media (max-width: 720px) {
  .product-summary {
    flex-direction: column;
    gap: 1.25rem;
  }

  .product-media {
    flex: 0 0 240px;
    align-self: center;
  }

  .sku-table {
    min-width: 0;
  }

  .sku-table colgroup,
  .sku-table thead {
    display: none;
  }

  .sku-table,
  .sku-table tbody,
  .sku-table tr,
  .sku-table td {
    display: block;
    width: 100%;
  }

  .sku-table tr {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    border-bottom: 1px solid #d0d0d0;
  }

  .sku-table tr:last-child {
    border-bottom: 0;
  }

  .sku-table td {
    border: 0;
  }

  .sku-description {
    grid-column: 1 / -1;
    background: #fafafa;
  }

  .sku-centered,
  .sku-delivery-cell,
  .price-cell {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
  }

  .mobile-label {
    display: block;
    color: #666;
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
  }

  .parts-modal__content {
    grid-template-columns: 1fr;
    padding: 1rem;
  }

  .parts-modal__preview,
  .parts-modal__preview img {
    min-height: 200px;
    height: 200px;
  }
}
</style>
